// ⚠️ GENERATED COPY — DO NOT EDIT.
// Source: shared/lib/textFitUtils.ts — edit there, then run: bash scripts/sync_shared_code.sh
// CI verifies these copies match the source (sync_shared_code.sh --check).
// textFitUtils — shrink overlaid field text so it stays inside the PDF bbox.
//
// Form field boxes are sized by the source PDF, not by our content. A long
// value (a rationale paragraph, "walking limited by bilateral knee OA", a long
// street address) renders past the box edge and gets clipped — the physician
// sees a sentence cut off mid-word. These helpers pick the largest font size at
// which the text still fits the box, so it shrinks-to-fit instead of clipping.
// Always pair with a `title={fullText}` so the untruncated value is on hover.
//
// Geometry is approximate (we don't measure glyphs): Inter's average advance is
// ~0.55em. Tune CHAR_W if a different font is used.

const CHAR_W = 0.56   // average glyph advance as a fraction of font size
const LINE_H = 1.3    // line-height multiple used by the textarea overlay

// Mirror of the burned-PDF renderer's wrap rule (generate_filled_pdf_bytes in
// med_form_app/backend/routers/forms_crud.py): a text box at least this many
// PDF POINTS tall gets wrapping insert_textbox treatment in the sent PDF;
// shorter boxes get single-line shrink + ellipsis. The on-screen overlays must
// take the same branch from the same geometry — the stored bbox, not rendered
// pixels, which drift with zoom.
export const PDF_WRAP_MIN_BOX_PT = 22

/**
 * True when the sent PDF will WRAP a value in a box of this bbox height
 * (PDF points, bbox[3] - bbox[1]). Overlays render such boxes as wrapping
 * textareas so the screen matches the insurer copy; anything shorter stays a
 * single-line input, matching the PDF's shrink-then-ellipsis path.
 */
export function wrapsInSentPdf(bboxHeightPt: number): boolean {
  return bboxHeightPt >= PDF_WRAP_MIN_BOX_PT
}

/**
 * Largest font size (px) at which a SINGLE-LINE value fits the box width.
 * Never grows past `maxPx`; never shrinks below `minPx`.
 */
export function fitFontSize(
  text: unknown,
  widthPx: number,
  { maxPx = 14, minPx = 7, padX = 8 }: { maxPx?: number; minPx?: number; padX?: number } = {}
): number {
  const len = text ? String(text).length : 0
  if (!len || !widthPx) return maxPx
  const usable = Math.max(1, widthPx - padX)
  const byWidth = usable / (len * CHAR_W)
  return Math.max(minPx, Math.min(maxPx, byWidth))
}

/**
 * Largest font size (px) at which a WRAPPING value fits the box area, so a
 * multi-line rationale fills the box top-to-bottom rather than overflowing it.
 */
export function fitTextareaFontSize(
  text: unknown,
  widthPx: number,
  heightPx: number,
  { maxPx = 14, minPx = 8, padX = 12, padY = 8 }: { maxPx?: number; minPx?: number; padX?: number; padY?: number } = {}
): number {
  const str = text ? String(text) : ''
  if (!str.length || !widthPx || !heightPx) return maxPx
  const usableW = Math.max(1, widthPx - padX)
  const usableH = Math.max(1, heightPx - padY)
  // Respect explicit line breaks: a prescriber "Name & Address" block (name on
  // one line, full address on the next) wraps each segment independently, so a
  // forced break must count as a new row. Treating the value as one flowing
  // paragraph undercounts rows and picks a font that overflows the box.
  const segments = str.split('\n')
  for (let fs = maxPx; fs >= minPx; fs -= 0.5) {
    const charsPerLine = Math.max(1, Math.floor(usableW / (fs * CHAR_W)))
    let lines = 0
    for (const seg of segments) {
      lines += Math.max(1, Math.ceil(seg.length / charsPerLine))  // empty line still takes a row
    }
    if (lines * fs * LINE_H <= usableH) return fs
  }
  return minPx
}

/**
 * True when a wrapping value still won't fit the box even at the legible floor
 * (`minPx`) — i.e. it will be cut off in the box and continued on the attached
 * page in the sent PDF. Mirrors fitTextareaFontSize's geometry at minPx, and
 * the burned PDF's 6pt floor (generate_filled_pdf_bytes), so the on-screen
 * overlay and the sent PDF agree on which fields continue.
 */
export function textareaOverflows(
  text: unknown,
  widthPx: number,
  heightPx: number,
  { minPx = 6, padX = 12, padY = 8 }: { minPx?: number; padX?: number; padY?: number } = {}
): boolean {
  const str = text ? String(text) : ''
  if (!str.length || !widthPx || !heightPx) return false
  const usableW = Math.max(1, widthPx - padX)
  const usableH = Math.max(1, heightPx - padY)
  const charsPerLine = Math.max(1, Math.floor(usableW / (minPx * CHAR_W)))
  let lines = 0
  for (const seg of str.split('\n')) {
    lines += Math.max(1, Math.ceil(seg.length / charsPerLine))
  }
  return lines * minPx * LINE_H > usableH
}
